import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FormControl,
    TextField,
    CircularProgress,
    makeStyles,
} from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useLazyGetFilteredProsjektQuery } from '../../../services/rtkApi';
import { SuperOfficeProsjektSelectNotFoundAlert } from './SuperOfficeProsjektSelectNotFoundAlert';
import type { ISoProsjektDto } from '../../../services/types';
import { PROSJEKT_SKJEMA_TYPE_IDS } from '@klp/prosjekt/interfaces';

const useStyles = makeStyles((theme) => ({
    formField: {
        '& .MuiTypography-root': {
            color: theme.palette.text.primary,
        },
        '& .Mui-disabled': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
}));

export interface SuperOfficeProsjektSelectProps {
    selectedProsjektSkjemaTypeId?: number;
    value?: ISoProsjektDto | null;
    touched?: boolean;
    error?: string;
    onChange: (value: ISoProsjektDto | null) => void;
}

export const SuperOfficeProsjektSelect: React.FC<
    SuperOfficeProsjektSelectProps
> = ({ selectedProsjektSkjemaTypeId, value, touched, error, onChange }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const [projectNameFilter, setProjectNameFilter] = useState('');

    const [
        filterSoProjects,
        {
            isLoading: superofficeProsjektIsLoading,
            isFetching: superofficeProsjektIsFetching,
            data: superofficeProsjektData,
        },
    ] = useLazyGetFilteredProsjektQuery();

    const isUnderprosjekt =
        selectedProsjektSkjemaTypeId === PROSJEKT_SKJEMA_TYPE_IDS.UNDERPROSJEKT;

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            if (projectNameFilter.length > 2) {
                filterSoProjects({
                    name: projectNameFilter,
                    filterExisting: false,
                });
            }
        }, 1000);

        return () => clearTimeout(delayDebounceFn);
    }, [projectNameFilter, filterSoProjects]);

    const isLoading =
        superofficeProsjektIsLoading || superofficeProsjektIsFetching;

    return (
        <>
            <SuperOfficeProsjektSelectNotFoundAlert
                searchTerm={projectNameFilter}
                isLoading={isLoading}
                data={superofficeProsjektData}
            />
            <FormControl
                required={true}
                fullWidth={true}
                margin="normal"
                error={touched && !!error}
            >
                <Autocomplete
                    id="superOfficeProsjektId"
                    className={classes.formField}
                    value={value ?? null}
                    options={superofficeProsjektData || []}
                    getOptionLabel={(option) =>
                        `${option.navn || ''}${
                            option.hasProjectProcess && !isUnderprosjekt
                                ? t('project.opprettProsjekt.occupiedProject')
                                : ''
                        }`
                    }
                    getOptionDisabled={(option) =>
                        !isUnderprosjekt && !!option.hasProjectProcess
                    }
                    onInputChange={(event, newInputValue, reason) => {
                        if (reason === 'input') {
                            setProjectNameFilter(newInputValue);
                        }
                    }}
                    onChange={(event, newValue) => onChange(newValue)}
                    loading={isLoading}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            required={true}
                            label={t(
                                'project.opprettProsjekt.superOfficeProsjekt',
                            )}
                            placeholder={t(
                                'project.opprettProsjekt.superOfficeProsjektPlaceholder',
                            )}
                            variant="outlined"
                            error={touched && !!error}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {isLoading ? (
                                            <CircularProgress color="inherit" />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </FormControl>
        </>
    );
};
